import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import FilterPanel from './components/FilterPanel';
import SubmissionsTable from './components/SubmissionsTable';
import StatsBar from './components/StatsBar';

const PAGE_SIZE = 15;

const NordPassAdminDashboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [replyLoading, setReplyLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    dateRange: 'all',
    optinEmail: 'all',
    optinSms: 'all',
    readStatus: 'all'
  });
  // Local read state (since table has no is_read column, we track in memory)
  const [readIds, setReadIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('admin_read_ids') || '[]')); }
    catch { return new Set(); }
  });

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchErr } = await supabase?.from('contact_submissions')?.select('*')?.order('created_at', { ascending: false });

      if (fetchErr) throw fetchErr;

      const enriched = (data || [])?.map(s => ({
        ...s,
        is_read: readIds?.has(s?.id)
      }));
      setSubmissions(enriched);
    } catch (err) {
      setError(err?.message || 'Kunne ikke laste innleveringer');
    } finally {
      setLoading(false);
    }
  }, [readIds]);

  useEffect(() => {
    fetchSubmissions();
    const interval = setInterval(fetchSubmissions, 30000);
    return () => clearInterval(interval);
  }, [fetchSubmissions]);

  const handleMarkRead = useCallback((id) => {
    setReadIds(prev => {
      const next = new Set(prev);
      next?.add(id);
      localStorage.setItem('admin_read_ids', JSON.stringify([...next]));
      return next;
    });
    setSubmissions(prev => prev?.map(s => s?.id === id ? { ...s, is_read: true } : s));
  }, []);

  const handleMarkUnread = useCallback((id) => {
    setReadIds(prev => {
      const next = new Set(prev);
      next?.delete(id);
      localStorage.setItem('admin_read_ids', JSON.stringify([...next]));
      return next;
    });
    setSubmissions(prev => prev?.map(s => s?.id === id ? { ...s, is_read: false } : s));
  }, []);

  const handleToggleExpand = useCallback((id) => {
    setExpandedId(prev => prev === id ? null : id);
    // Auto-mark as read when expanded
    handleMarkRead(id);
  }, [handleMarkRead]);

  const handleReply = useCallback(async (id, replyText, email) => {
    setReplyLoading(true);
    try {
      // In a real scenario this would send an email via edge function
      // For now we log and mark as read
      console.log(`Reply to ${email}:`, replyText);
      handleMarkRead(id);
      await new Promise(r => setTimeout(r, 800)); // simulate send
    } finally {
      setReplyLoading(false);
    }
  }, [handleMarkRead]);

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const handleSearch = useCallback((q) => {
    setSearchQuery(q);
    setPage(1);
  }, []);

  const handleSelectAll = useCallback((checked) => {
    setSelectedIds(checked ? filteredSubmissions?.map(s => s?.id) : []);
  }, [filteredSubmissions]);

  const handleBulkMarkRead = () => {
    selectedIds?.forEach(id => handleMarkRead(id));
    setSelectedIds([]);
  };

  const handleExport = () => {
    const csv = [
      ['ID', 'Navn', 'E-post', 'Telefon', 'Melding', 'E-post samtykke', 'SMS samtykke', 'Tidspunkt'],
      ...filteredSubmissions?.map(s => [
        s?.id, s?.name, s?.email, s?.phone || '',
        `"${(s?.message || '')?.replace(/"/g, '""')}"`,
        s?.optin_email ? 'Ja' : 'Nei',
        s?.optin_sms ? 'Ja' : 'Nei',
        s?.created_at
      ])
    ]?.map(r => r?.join(','))?.join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kontaktskjema_${new Date()?.toISOString()?.slice(0, 10)}.csv`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  // Filter logic
  const now = new Date();
  const filteredSubmissions = submissions?.filter(s => {
    // Search
    if (searchQuery) {
      const q = searchQuery?.toLowerCase();
      const match = [
        s?.name, s?.email, s?.phone, s?.message
      ]?.some(v => v?.toLowerCase()?.includes(q));
      if (!match) return false;
    }
    // Date range
    if (filters?.dateRange !== 'all') {
      const created = new Date(s.created_at);
      const diffDays = (now - created) / (1000 * 60 * 60 * 24);
      if (filters?.dateRange === 'today' && diffDays > 1) return false;
      if (filters?.dateRange === 'week' && diffDays > 7) return false;
      if (filters?.dateRange === 'month' && diffDays > 30) return false;
      if (filters?.dateRange === 'quarter' && diffDays > 90) return false;
    }
    // Opt-in email
    if (filters?.optinEmail === 'yes' && !s?.optin_email) return false;
    if (filters?.optinEmail === 'no' && s?.optin_email) return false;
    // Opt-in SMS
    if (filters?.optinSms === 'yes' && !s?.optin_sms) return false;
    if (filters?.optinSms === 'no' && s?.optin_sms) return false;
    // Read status
    if (filters?.readStatus === 'read' && !s?.is_read) return false;
    if (filters?.readStatus === 'unread' && s?.is_read) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filteredSubmissions?.length / PAGE_SIZE));
  const pagedSubmissions = filteredSubmissions?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const stats = {
    total: submissions?.length,
    unread: submissions?.filter(s => !s?.is_read)?.length,
    optinEmail: submissions?.filter(s => s?.optin_email)?.length,
    optinSms: submissions?.filter(s => s?.optin_sms)?.length
  };

  return (
    <>
      <Helmet>
        <title>Admin Dashboard — NordPass Kontaktskjema</title>
        <link
          href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Lora:ital,wght@0,400;0,700;1,400;1,700&family=Inter:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <div style={{
        background: '#050A12',
        minHeight: '100vh',
        fontFamily: 'Inter, sans-serif'
      }}>
        {/* Top nav */}
        <nav style={{
          background: 'rgba(5,10,18,0.95)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          padding: '0 5%',
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
          height: '64px',
          position: 'sticky', top: 0, zIndex: 100
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <a href="/nord-pass-landing-page" style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              textDecoration: 'none'
            }}>
              <div style={{
                width: '28px', height: '28px',
                background: '#3B6FD4',
                clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))'
              }} />
              <span style={{
                fontFamily: 'Syne, sans-serif',
                fontSize: '1rem', fontWeight: 800,
                color: '#EEF0F2', letterSpacing: '-0.03em'
              }}>NordPass</span>
            </a>
            <div style={{
              width: '1px', height: '20px',
              background: 'rgba(255,255,255,0.1)'
            }} />
            <div style={{
              fontFamily: 'monospace', fontSize: '0.55rem',
              letterSpacing: '0.25em', color: '#E8941A',
              textTransform: 'uppercase'
            }}>ADMIN DASHBOARD</div>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {selectedIds?.length > 0 && (
              <button
                onClick={handleBulkMarkRead}
                style={{
                  background: 'rgba(59,111,212,0.15)',
                  border: '1px solid rgba(59,111,212,0.4)',
                  color: '#3B6FD4',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontFamily: 'monospace', fontSize: '0.55rem',
                  letterSpacing: '0.1em', textTransform: 'uppercase'
                }}
              >MERK {selectedIds?.length} SOM LEST</button>
            )}
            <button
              onClick={handleExport}
              style={{
                background: 'rgba(232,148,26,0.1)',
                border: '1px solid rgba(232,148,26,0.3)',
                color: '#E8941A',
                padding: '8px 16px',
                cursor: 'pointer',
                fontFamily: 'monospace', fontSize: '0.55rem',
                letterSpacing: '0.1em', textTransform: 'uppercase',
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(232,148,26,0.2)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(232,148,26,0.1)'; }}
            >↓ EKSPORTER CSV</button>
            <button
              onClick={fetchSubmissions}
              style={{
                background: 'rgba(59,111,212,0.1)',
                border: '1px solid rgba(59,111,212,0.3)',
                color: '#3B6FD4',
                padding: '8px 16px',
                cursor: 'pointer',
                fontFamily: 'monospace', fontSize: '0.55rem',
                letterSpacing: '0.1em', textTransform: 'uppercase',
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(59,111,212,0.2)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(59,111,212,0.1)'; }}
            >↺ OPPDATER</button>
          </div>
        </nav>

        {/* Main content */}
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '48px 5%' }}>
          {/* Page header */}
          <div style={{ marginBottom: '40px', position: 'relative' }}>
            <div style={{
              position: 'absolute', top: '-20px', left: '-8px',
              fontFamily: '"Arial Black", sans-serif',
              fontSize: '10rem', fontWeight: 900,
              color: 'rgba(59,111,212,0.04)',
              lineHeight: 1, userSelect: 'none', pointerEvents: 'none',
              letterSpacing: '-0.06em'
            }}>ADMIN</div>
            <div style={{
              fontFamily: 'monospace', fontSize: '0.5rem',
              letterSpacing: '0.35em', color: '#E8941A',
              textTransform: 'uppercase', marginBottom: '8px'
            }}>// KONTAKTSKJEMA ADMINISTRASJON</div>
            <h1 style={{
              fontFamily: '"Arial Black", sans-serif',
              fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
              fontWeight: 900, color: '#EEF0F2',
              letterSpacing: '-0.04em', textTransform: 'uppercase',
              lineHeight: 0.9, margin: 0
            }}>INNLEVERINGER</h1>
            <div style={{
              fontFamily: 'Georgia, serif', fontStyle: 'italic',
              fontSize: '1.1rem', color: '#3B6FD4',
              marginTop: '8px'
            }}>Administrer og svar på kontaktskjema-innleveringer.</div>
          </div>

          {/* Stats bar */}
          <StatsBar stats={stats} />

          {/* Filter panel */}
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onSearch={handleSearch}
            searchQuery={searchQuery}
            totalCount={submissions?.length}
            filteredCount={filteredSubmissions?.length}
          />

          {/* Error state */}
          {error && (
            <div style={{
              background: 'rgba(220,38,38,0.1)',
              border: '1px solid rgba(220,38,38,0.3)',
              borderLeft: '4px solid #DC2626',
              padding: '16px 20px',
              marginBottom: '24px',
              fontFamily: 'monospace', fontSize: '0.7rem',
              color: '#FCA5A5', letterSpacing: '0.05em'
            }}>
              FEIL: {error}
            </div>
          )}

          {/* Loading state */}
          {loading ? (
            <div style={{
              background: '#0A1220',
              border: '1px solid rgba(59,111,212,0.15)',
              padding: '64px 32px',
              textAlign: 'center'
            }}>
              <div style={{
                fontFamily: 'monospace', fontSize: '0.6rem',
                letterSpacing: '0.3em', color: '#3B6FD4',
                textTransform: 'uppercase',
                animation: 'pulse 1.5s ease-in-out infinite'
              }}>LASTER INNLEVERINGER...</div>
              <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
            </div>
          ) : (
            <SubmissionsTable
              submissions={pagedSubmissions}
              expandedId={expandedId}
              onToggleExpand={handleToggleExpand}
              onMarkRead={handleMarkRead}
              onMarkUnread={handleMarkUnread}
              onReply={handleReply}
              replyLoading={replyLoading}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              onSelectAll={handleSelectAll}
              selectedIds={selectedIds}
              onSelectRow={(id, checked) => setSelectedIds(prev =>
                checked ? [...prev, id] : prev?.filter(x => x !== id)
              )}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default NordPassAdminDashboard;
